import { useCallback } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { isStorageResourceNodeType } from '../../explorer/models/azureStorageNodeFactories';
import type { StorageServiceFolderNode } from '../../explorer/models/nodeTypes';
import { getStorageFallbackQueryClient, loadStorageServiceFolder } from '../services/azureStorageListing';
import {
  ConnectionTreeNode,
  DatabaseExplorerNode,
  FolderNodeBaseMetadata,
  FolderTreeNode,
  DatabaseObjectTreeNode,
  ColumnsFolderNode,
  KeysFolderNode,
  ConstraintsFolderNode,
  IndexesFolderNode,
  TriggersFolderNode,
  StatisticsFolderNode,
  PartitionsFolderNode,
  isDatabaseObjectNode,
  isColumnsFolderNode,
  isKeysFolderNode,
  isConstraintsFolderNode,
  isIndexesFolderNode,
  isTriggersFolderNode,
  isStatisticsFolderNode,
  isPartitionsFolderNode,
} from '../../explorer/models/DatabaseTreeModels';
import type { AgentJobsSubfolderNode } from '../../explorer/models/nodeTypes';
import { findNodeById } from '../../explorer/services/treeService';
import { DatabaseObject, DatabaseObjectType } from '../models/databaseObjectTypes';
import { mapFolderTypeToObjectType } from '../services/objectFolderUtils';
import {
  loadObjectFolder,
  loadTableSubfolders,
  loadColumnsFolder,
  loadKeysFolder,
  loadConstraintsFolder,
  loadIndexesFolder,
  loadTriggersFolder,
  loadStatisticsFolder,
  loadPartitionsFolder,
  loadSecurityFolder,
  loadAgentSubfolder,
} from '../services/nodeExpansionService';
import { loadViewSubfolders } from '../services/viewExpansion';
import { loadPackageMembers } from '../services/packageExpansion';
import type { DatabaseEngineId } from '../../../core/engines/engineTypes';
import { isSecurityLeafFolderNodeType } from '../services/securityFolderExpansion';
import {
  expandPinnedDatabase,
  expandPinnedSchema,
  expandPinnedFolder,
  expandPinnedObject,
  wrapUpdateNodeForPinnedDescendant,
} from '../services/pinnedExpansion';
import type {
  PinnedDatabaseNode,
  PinnedSchemaNode,
  PinnedFolderNode,
  PinnedObjectNode,
} from '../../explorer/models/nodeTypes';

/** Track node expansion event (node type only, never node name for privacy) */
const trackNodeExpand = (nodeType: string) => {
  if (typeof window !== 'undefined' && window.electronAPI?.analytics?.track) {
    window.electronAPI.analytics.track(`explorer:node_expand:${nodeType}`).catch(() => {});
  }
};

interface NodeExpansionOptions {
  treeData: DatabaseExplorerNode[];
  fetchObjectsForFolder: (
    objectType: DatabaseObjectType,
    connectionId: string,
    database: string,
  ) => Promise<DatabaseObject[]>;
  toggleNodeExpanded: (nodeId: string) => boolean;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  addDatabaseObjects: (connectionId: string, database: string, objects: DatabaseObject[]) => void;
  setLoadingObject: (connectionId: string, database: string, objectType: string, isLoading: boolean) => void;
  getPreloadedObjects: (connectionId: string, database: string) => DatabaseObject[] | undefined;
  /**
   * Runs a databases-folder listing. Owned by `DatabaseExplorer` because a
   * listing is fenced on the connect generation it was STARTED in — an
   * unfenced `loadDatabasesFolder` here would let an expand issued before a
   * disconnect settle afterwards and overwrite the successor's folder (or,
   * once the connect end cancels the query, write an error + Retry tile into a
   * connection that is gone).
   */
  /**
   * "The connect this expand was started for is over." Supplied by
   * `DatabaseExplorer`, which owns the connect generations; every listing this
   * hook starts is fenced on it (see `objectFolderExpansion`).
   */
  isConnectStale?: (connectionId: string) => () => boolean;
  listDatabasesFolder?: (args: {
    node: FolderTreeNode;
    connectionId: string;
    engine?: DatabaseEngineId;
    forceReload?: boolean;
    onFetchError?: (connectionId: string, errorMessage: string) => void;
  }) => Promise<void>;
  /** Called when a fetch error occurs - can be used to detect connection loss */
  onFetchError?: (connectionId: string, errorMessage: string) => void;
  /** The app's QueryClient (Azure Storage listings share its cache with the grid and the pin resolver). */
  queryClient?: QueryClient;
}

export function useNodeExpansion({
  treeData,
  isConnectStale,
  listDatabasesFolder,
  fetchObjectsForFolder,
  toggleNodeExpanded,
  updateNode,
  addDatabaseObjects,
  setLoadingObject,
  getPreloadedObjects,
  onFetchError,
  queryClient: providedQueryClient,
}: NodeExpansionOptions) {
  const queryClient = providedQueryClient ?? getStorageFallbackQueryClient();
  const handleToggleExpand = useCallback(
    (nodeId: string) => {
      const isExpanded = toggleNodeExpanded(nodeId);
      if (!isExpanded) return;

      const node = findNodeById(nodeId, treeData) as
        | DatabaseExplorerNode
        | ConnectionTreeNode
        | FolderTreeNode
        | DatabaseObjectTreeNode
        | undefined;

      if (!node || node.type === 'connection') {
        return;
      }

      // Track node expansion (type only, never node name for privacy)
      trackNodeExpand(node.type);

      // Azure Storage (spec §5.1): a service folder lists its resources through
      // the storage listing service (TanStack Query over `azure-storage:list`),
      // never a SQL path; resource rows are leaves. Dispatched BEFORE every
      // SQL branch so the generic `-folder` handling never sees them.
      if (node.type === 'storage-service-folder') {
        const folder = node as StorageServiceFolderNode;
        const staleCheck = isConnectStale?.(folder.metadata.connectionId);
        void loadStorageServiceFolder({
          node: folder,
          queryClient,
          updateNode,
          ...(staleCheck ? { isCurrent: () => !staleCheck() } : {}),
        });
        return;
      }
      if (isStorageResourceNodeType(node.type) || node.type === 'pinned-storage') {
        return;
      }

      // 2026-05-26 user override: pinned-* wrappers are full drill-in nodes
      // now. Dispatch to the pinned-aware loader in `pinnedExpansion.ts`,
      // which reuses the canonical loaders and rewrites every emitted
      // descendant id into the `pinned-desc<->...` namespace.
      if (node.type === 'pinned-database') {
        // Engine lives on the pinned wrapper's metadata (stamped at build
        // time from the parent connection node).
        const engine = (node.metadata as { engine?: DatabaseEngineId }).engine;
        expandPinnedDatabase({
          node: node as PinnedDatabaseNode,
          engine,
          updateNode,
        });
        return;
      }

      if (node.type === 'pinned-schema') {
        expandPinnedSchema({
          node: node as PinnedSchemaNode,
          getPreloadedObjects,
          updateNode,
        });
        return;
      }

      if (node.type === 'pinned-folder') {
        const pinnedConnectionId = (node.metadata as { connectionId?: string }).connectionId;
        void expandPinnedFolder({
          node: node as PinnedFolderNode,
          fetchObjectsForFolder,
          addDatabaseObjects,
          setLoadingObject,
          getPreloadedObjects,
          updateNode,
          onFetchError,
          ...(isConnectStale && pinnedConnectionId
            ? { isStale: isConnectStale(pinnedConnectionId) }
            : {}),
        });
        return;
      }

      if (node.type === 'pinned-object') {
        expandPinnedObject({
          node: node as PinnedObjectNode,
          updateNode,
        });
        return;
      }

      // Finding P1 — everything below dispatches by canonical `node.type`. When
      // the node being expanded is itself a pinned descendant (a folder / table
      // / sub-folder living under a pinned wrapper, keeping its canonical type),
      // route the canonical loader's `updateNode` through this wrapper so the
      // children it emits stay in the `pinned-desc<->...` id namespace instead
      // of leaking canonical ids that collide with the Databases drill-down.
      // For every non-pinned-descendant node this is the original `updateNode`.
      const effectiveUpdateNode = wrapUpdateNodeForPinnedDescendant(node.id, updateNode);

      // Handle databases folder expansion
      if (node.type === 'databases-folder') {
        const connectionId = (node.metadata as FolderNodeBaseMetadata)?.connectionId;
        if (!connectionId) {
          return;
        }

        // Get engine from the parent connection node
        const parentConnectionNode = treeData.find(
          (n) => n.type === 'connection' && (n as ConnectionTreeNode).metadata?.id === connectionId
        ) as ConnectionTreeNode | undefined;
        const engine = parentConnectionNode?.metadata?.engine as DatabaseEngineId | undefined;

        const folderNode = node as FolderTreeNode;
        void listDatabasesFolder?.({
          node: folderNode,
          connectionId,
          engine,
          ...(onFetchError ? { onFetchError } : {}),
        });
        return;
      }

      // Handle table node expansion - show subfolders
      if (node.type === 'table' && isDatabaseObjectNode(node)) {
        loadTableSubfolders({
          node: node as DatabaseObjectTreeNode,
          updateNode: effectiveUpdateNode,
        });
        return;
      }

      // Handle view node expansion - show Columns subfolder
      if (node.type === 'view' && isDatabaseObjectNode(node)) {
        loadViewSubfolders({
          node: node as DatabaseObjectTreeNode,
          updateNode: effectiveUpdateNode,
        });
        return;
      }

      // Handle package node expansion - show member procedures/functions
      if (node.type === 'package' && isDatabaseObjectNode(node)) {
        void loadPackageMembers({
          node: node as DatabaseObjectTreeNode,
          updateNode: effectiveUpdateNode,
        });
        return;
      }

      // Handle columns folder expansion - fetch columns
      if (isColumnsFolderNode(node)) {
        void loadColumnsFolder({
          node: node as ColumnsFolderNode,
          updateNode: effectiveUpdateNode,
        });
        return;
      }

      // Handle keys folder expansion - fetch keys
      if (isKeysFolderNode(node)) {
        void loadKeysFolder({
          node: node as KeysFolderNode,
          updateNode: effectiveUpdateNode,
        });
        return;
      }

      // Handle constraints folder expansion - fetch constraints
      if (isConstraintsFolderNode(node)) {
        void loadConstraintsFolder({
          node: node as ConstraintsFolderNode,
          updateNode: effectiveUpdateNode,
        });
        return;
      }

      // Handle indexes folder expansion - fetch indexes
      if (isIndexesFolderNode(node)) {
        void loadIndexesFolder({
          node: node as IndexesFolderNode,
          updateNode: effectiveUpdateNode,
        });
        return;
      }

      // Handle triggers folder expansion - fetch triggers
      if (isTriggersFolderNode(node)) {
        void loadTriggersFolder({
          node: node as TriggersFolderNode,
          updateNode: effectiveUpdateNode,
        });
        return;
      }

      // Handle statistics folder expansion - fetch statistics
      if (isStatisticsFolderNode(node)) {
        void loadStatisticsFolder({
          node: node as StatisticsFolderNode,
          updateNode: effectiveUpdateNode,
        });
        return;
      }

      // Handle partitions folder expansion - fetch child partitions (pgsql)
      if (isPartitionsFolderNode(node)) {
        void loadPartitionsFolder({
          node: node as PartitionsFolderNode,
          updateNode: effectiveUpdateNode,
        });
        return;
      }

      // Handle agent subfolders (Operators, etc.)
      if (node.type === 'agent-jobs-subfolder' && node.canHaveChildren) {
        void loadAgentSubfolder({
          node: node as AgentJobsSubfolderNode,
          updateNode,
          onFetchError,
        });
        return;
      }

      // Handle security folders (logins/users/roles/schemas, etc.)
      if (isSecurityLeafFolderNodeType(node.type)) {
        void loadSecurityFolder({
          node: node as FolderTreeNode,
          updateNode,
          onFetchError,
        });
        return;
      }

      // Handle database object folders (tables, views, etc.)
      const objectType = mapFolderTypeToObjectType(node.type);
      if (!objectType) {
        return;
      }

      const metadata = node.metadata as FolderNodeBaseMetadata;
      if (!metadata?.connectionId) {
        return;
      }

      const folderNode = node as FolderTreeNode;
      void loadObjectFolder({
        node: folderNode,
        connectionId: metadata.connectionId,
        database: metadata.database,
        objectType,
        fetchObjectsForFolder,
        addDatabaseObjects,
        setLoadingObject,
        updateNode: effectiveUpdateNode,
        getPreloadedObjects,
        onFetchError,
        ...(isConnectStale ? { isStale: isConnectStale(metadata.connectionId) } : {}),
      });
    },
    [
      addDatabaseObjects,
      isConnectStale,
      listDatabasesFolder,
      fetchObjectsForFolder,
      getPreloadedObjects,
      onFetchError,
      queryClient,
      setLoadingObject,
      toggleNodeExpanded,
      treeData,
      updateNode,
    ],
  );

  return {
    handleToggleExpand,
  };
}
